import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, ExternalLink, Search, Filter, Grid3X3, List,
  Scissors, Briefcase, Dumbbell, UtensilsCrossed, Home,
  Car, Palette, Heart, GraduationCap, Building2, Plane,
  Scale, Sun, TreeDeciduous, Hammer, Sparkles, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

// Premium demos data with metadata
const PREMIUM_DEMOS = [
  {
    id: 'smp-treatment',
    file: 'demo-smp-treatment.html',
    name: 'SMP Treatment',
    sector: 'Micropigmentación Capilar',
    category: 'beauty',
    description: 'Landing profesional para clínicas de micropigmentación capilar (SMP)',
    tags: ['salud', 'estética', 'tratamiento', 'calvicie'],
    icon: Sparkles
  },
  {
    id: 'book-coaching',
    file: 'demo-book-coaching.html',
    name: 'Book Coaching',
    sector: 'Coaching & Desarrollo Personal',
    category: 'education',
    description: 'Para coaches y escritores que venden programas de mentoría',
    tags: ['coaching', 'libro', 'mentoría', 'desarrollo'],
    icon: GraduationCap
  },
  {
    id: 'cleaning-b2b',
    file: 'demo-cleaning-b2b.html',
    name: 'Cleaning B2B',
    sector: 'Limpieza Profesional',
    category: 'services',
    description: 'Servicios de limpieza para empresas y oficinas',
    tags: ['limpieza', 'b2b', 'empresas', 'oficinas'],
    icon: Home
  },
  {
    id: 'fitness-gym',
    file: 'demo-fitness-gym.html',
    name: 'Fitness Gym',
    sector: 'Gimnasio & Fitness',
    category: 'health',
    description: 'Gimnasio con membresías y clases grupales',
    tags: ['gimnasio', 'fitness', 'entrenamiento', 'salud'],
    icon: Dumbbell
  },
  {
    id: 'natural-health',
    file: 'demo-natural-health.html',
    name: 'Natural Health',
    sector: 'Salud Natural',
    category: 'health',
    description: 'Productos naturales y suplementos de salud',
    tags: ['salud', 'natural', 'suplementos', 'bienestar'],
    icon: Heart
  },
  {
    id: 'pizzeria',
    file: 'demo-pizzeria.html',
    name: 'Pizzería',
    sector: 'Restaurante',
    category: 'food',
    description: 'Pizzería con menú y pedidos online',
    tags: ['pizza', 'restaurante', 'comida', 'delivery'],
    icon: UtensilsCrossed
  },
  {
    id: 'fitness-meals',
    file: 'demo-fitness-meals.html',
    name: 'Fitness Meals',
    sector: 'Comida Saludable',
    category: 'food',
    description: 'Meal prep y comidas fitness personalizadas',
    tags: ['comida', 'fitness', 'meal prep', 'dieta'],
    icon: UtensilsCrossed
  },
  {
    id: 'distilling-course',
    file: 'demo-distilling-course.html',
    name: 'Distilling Course',
    sector: 'Cursos & Formación',
    category: 'education',
    description: 'Curso de destilación artesanal',
    tags: ['curso', 'destilación', 'formación', 'artesanal'],
    icon: GraduationCap
  },
  {
    id: 'saas-accounting',
    file: 'demo-saas-accounting.html',
    name: 'SaaS Accounting',
    sector: 'Software / SaaS',
    category: 'tech',
    description: 'Software de contabilidad para autónomos y pymes',
    tags: ['saas', 'software', 'contabilidad', 'finanzas'],
    icon: Briefcase
  },
  {
    id: 'sales-system',
    file: 'demo-sales-system.html',
    name: 'Sales System',
    sector: 'Formación en Ventas',
    category: 'education',
    description: 'Sistema y curso de ventas para empresarios',
    tags: ['ventas', 'formación', 'negocios', 'sistema'],
    icon: Briefcase
  },
  {
    id: 'solar-installation',
    file: 'demo-solar-installation.html',
    name: 'Solar Installation',
    sector: 'Energía Solar',
    category: 'services',
    description: 'Instalación de paneles solares y energía renovable',
    tags: ['solar', 'energía', 'renovable', 'instalación'],
    icon: Sun
  },
  {
    id: 'cake-course',
    file: 'demo-cake-course.html',
    name: 'Cake Course',
    sector: 'Repostería & Cursos',
    category: 'education',
    description: 'Curso de repostería y decoración de tartas',
    tags: ['repostería', 'curso', 'tartas', 'pastelería'],
    icon: UtensilsCrossed
  },
  {
    id: 'yoga-retreat',
    file: 'demo-yoga-retreat.html',
    name: 'Yoga Retreat',
    sector: 'Wellness & Retiros',
    category: 'health',
    description: 'Retiros de yoga y bienestar',
    tags: ['yoga', 'retiro', 'wellness', 'meditación'],
    icon: Heart
  },
  {
    id: 'hustler-agriculture',
    file: 'demo-hustler-agriculture.html',
    name: 'Hustler Agriculture',
    sector: 'Agricultura & Maquinaria',
    category: 'industry',
    description: 'Maquinaria agrícola y equipamiento',
    tags: ['agricultura', 'maquinaria', 'campo', 'equipos'],
    icon: TreeDeciduous
  },
  {
    id: 'unikorn-realestate',
    file: 'demo-unikorn-realestate.html',
    name: 'Unikorn Real Estate',
    sector: 'Inmobiliaria',
    category: 'realestate',
    description: 'Agencia inmobiliaria moderna',
    tags: ['inmobiliaria', 'propiedades', 'casas', 'venta'],
    icon: Building2
  },
  {
    id: 'hypnosis-smoking',
    file: 'demo-hypnosis-smoking.html',
    name: 'Hypnosis - Stop Smoking',
    sector: 'Terapia & Hipnosis',
    category: 'health',
    description: 'Hipnoterapia para dejar de fumar',
    tags: ['hipnosis', 'terapia', 'tabaco', 'salud'],
    icon: Heart
  },
  {
    id: 'lovekitchen-events',
    file: 'demo-lovekitchen-events.html',
    name: 'Love Kitchen Events',
    sector: 'Catering & Eventos',
    category: 'food',
    description: 'Catering y organización de eventos',
    tags: ['catering', 'eventos', 'bodas', 'celebraciones'],
    icon: UtensilsCrossed
  },
  {
    id: 'artisan-painting',
    file: 'demo-artisan-painting.html',
    name: 'Artisan Painting',
    sector: 'Pintura & Decoración',
    category: 'services',
    description: 'Servicios de pintura artesanal y decoración',
    tags: ['pintura', 'decoración', 'artesanal', 'hogar'],
    icon: Palette
  },
  {
    id: 'orangetheory-fitness',
    file: 'demo-orangetheory-fitness.html',
    name: 'OrangeTheory Fitness',
    sector: 'Fitness Boutique',
    category: 'health',
    description: 'Estudio de fitness con entrenamientos HIIT',
    tags: ['fitness', 'hiit', 'entrenamiento', 'boutique'],
    icon: Dumbbell
  },
  {
    id: 'carusos-health',
    file: 'demo-carusos-health.html',
    name: "Caruso's Health",
    sector: 'Suplementos Naturales',
    category: 'health',
    description: 'Tienda de suplementos y vitaminas naturales',
    tags: ['suplementos', 'vitaminas', 'salud', 'natural'],
    icon: Heart
  },
  {
    id: 'goodvibes-getaways',
    file: 'demo-goodvibes-getaways.html',
    name: 'Good Vibes Getaways',
    sector: 'Agencia de Viajes',
    category: 'travel',
    description: 'Agencia de viajes y experiencias',
    tags: ['viajes', 'turismo', 'experiencias', 'vacaciones'],
    icon: Plane
  },
  {
    id: 'primal-alternative',
    file: 'demo-primal-alternative.html',
    name: 'Primal Alternative',
    sector: 'Alimentación Paleo/Primal',
    category: 'food',
    description: 'Productos de alimentación paleo y primal',
    tags: ['paleo', 'primal', 'alimentación', 'salud'],
    icon: UtensilsCrossed
  },
  {
    id: 'raywhite-realestate',
    file: 'demo-raywhite-realestate.html',
    name: 'Ray White Real Estate',
    sector: 'Inmobiliaria Premium',
    category: 'realestate',
    description: 'Agencia inmobiliaria de lujo',
    tags: ['inmobiliaria', 'lujo', 'premium', 'propiedades'],
    icon: Building2
  },
  {
    id: 'renovating-profit',
    file: 'demo-renovating-profit.html',
    name: 'Renovating for Profit',
    sector: 'Reformas & Inversión',
    category: 'services',
    description: 'Curso y servicios de reformas para inversores',
    tags: ['reformas', 'inversión', 'inmuebles', 'rentabilidad'],
    icon: Hammer
  },
  {
    id: 'slater-gordon-lawyers',
    file: 'demo-slater-gordon-lawyers.html',
    name: 'Slater Gordon Lawyers',
    sector: 'Bufete de Abogados',
    category: 'legal',
    description: 'Despacho de abogados especializado',
    tags: ['abogados', 'legal', 'derecho', 'bufete'],
    icon: Scale
  }
];

// Categories for filtering
const CATEGORIES = [
  { id: 'all', label: 'Todos', count: PREMIUM_DEMOS.length },
  { id: 'health', label: 'Salud & Fitness', icon: Heart },
  { id: 'food', label: 'Alimentación', icon: UtensilsCrossed },
  { id: 'services', label: 'Servicios', icon: Home },
  { id: 'education', label: 'Formación', icon: GraduationCap },
  { id: 'realestate', label: 'Inmobiliaria', icon: Building2 },
  { id: 'tech', label: 'Tech / SaaS', icon: Briefcase },
  { id: 'travel', label: 'Viajes', icon: Plane },
  { id: 'legal', label: 'Legal', icon: Scale },
  { id: 'industry', label: 'Industria', icon: TreeDeciduous }
];

export function PremiumDemosGallery({ onSelectDemo = null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [copiedId, setCopiedId] = useState(null);

  // Filter demos based on search and category
  const filteredDemos = useMemo(() => {
    return PREMIUM_DEMOS.filter(demo => {
      const matchesSearch = searchTerm === '' || 
        demo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demo.sector.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demo.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || demo.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  // Get category counts
  const categoryCounts = useMemo(() => {
    const counts = { all: PREMIUM_DEMOS.length };
    PREMIUM_DEMOS.forEach(demo => {
      counts[demo.category] = (counts[demo.category] || 0) + 1;
    });
    return counts;
  }, []);

  const getPreviewUrl = (file) => {
    // Use production domain
    return `https://lokalgrow.com/webs-demo/premium/${file}`;
  };

  const copyUrl = async (demo) => {
    const url = getPreviewUrl(demo.file);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(demo.id);
      toast.success('URL copiada al portapapeles');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const handleSelectDemo = (demo) => {
    if (onSelectDemo) {
      onSelectDemo(demo);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Globe className="h-6 w-6 text-emerald-400" />
            Galería de Demos Premium
          </h2>
          <p className="text-gray-400 mt-1">
            {PREMIUM_DEMOS.length} templates profesionales listos para personalizar
          </p>
        </div>
        
        {/* View toggle */}
        <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por nombre, sector o etiqueta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              selectedCategory === cat.id
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            {cat.label}
            <span className="ml-1.5 text-xs opacity-70">
              ({categoryCounts[cat.id] || 0})
            </span>
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500">
        Mostrando {filteredDemos.length} de {PREMIUM_DEMOS.length} demos
      </p>

      {/* Grid/List View */}
      <AnimatePresence mode="wait">
        {viewMode === 'grid' ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {filteredDemos.map((demo, index) => {
              const IconComponent = demo.icon;
              return (
                <motion.div
                  key={demo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all group"
                >
                  {/* Preview thumbnail area */}
                  <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 relative flex items-center justify-center">
                    <IconComponent className="h-12 w-12 text-gray-600 group-hover:text-emerald-500 transition-colors" />
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <a
                        href={getPreviewUrl(demo.file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-emerald-600 rounded-lg hover:bg-emerald-500 transition-colors"
                        title="Ver demo"
                      >
                        <ExternalLink className="h-5 w-5 text-white" />
                      </a>
                      <button
                        onClick={() => copyUrl(demo)}
                        className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                        title="Copiar URL"
                      >
                        {copiedId === demo.id ? (
                          <Check className="h-5 w-5 text-emerald-400" />
                        ) : (
                          <Copy className="h-5 w-5 text-white" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-white truncate">{demo.name}</h3>
                    <p className="text-sm text-emerald-400 truncate">{demo.sector}</p>
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">{demo.description}</p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {demo.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-gray-700/50 text-gray-400 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Action button */}
                    {onSelectDemo && (
                      <Button
                        size="sm"
                        className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500"
                        onClick={() => handleSelectDemo(demo)}
                      >
                        Usar este template
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2"
          >
            {filteredDemos.map((demo, index) => {
              const IconComponent = demo.icon;
              return (
                <motion.div
                  key={demo.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-emerald-500/50 transition-all flex items-center gap-4"
                >
                  {/* Icon */}
                  <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-6 w-6 text-emerald-500" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{demo.name}</h3>
                      <span className="px-2 py-0.5 bg-emerald-900/50 text-emerald-400 text-xs rounded">
                        {demo.sector}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 truncate">{demo.description}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => copyUrl(demo)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                      title="Copiar URL"
                    >
                      {copiedId === demo.id ? (
                        <Check className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    <a
                      href={getPreviewUrl(demo.file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-emerald-600 rounded-lg hover:bg-emerald-500 transition-colors"
                      title="Ver demo"
                    >
                      <ExternalLink className="h-4 w-4 text-white" />
                    </a>
                    {onSelectDemo && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500"
                        onClick={() => handleSelectDemo(demo)}
                      >
                        Usar
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state */}
      {filteredDemos.length === 0 && (
        <div className="text-center py-12">
          <Globe className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No se encontraron demos</h3>
          <p className="text-gray-400">
            Intenta con otros términos de búsqueda o categoría
          </p>
        </div>
      )}

      {/* Help section */}
      <div className="bg-gradient-to-r from-emerald-900/30 to-cyan-900/30 border border-emerald-800/50 rounded-xl p-6 mt-8">
        <h3 className="text-lg font-semibold text-white mb-3">
          ¿Cómo instalar un demo a un cliente?
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
          <li>Ve a la pestaña <strong>"Solicitudes"</strong> y selecciona o crea una solicitud del cliente</li>
          <li>Haz clic en <strong>"Crear Sitio"</strong> desde la solicitud</li>
          <li>Elige el template que quieres usar (puedes copiar la URL desde esta galería)</li>
          <li>Configura el <strong>subdominio</strong> (ej: cliente.lokalgrow.com) o dominio propio</li>
          <li>Personaliza los datos del negocio: nombre, teléfono, colores, logo, etc.</li>
          <li>Activa el sitio y ¡listo! El cliente ya tiene su web funcionando</li>
        </ol>
        <p className="text-emerald-400 text-sm mt-4">
          💡 Tip: Los sitios se pueden editar en cualquier momento desde la pestaña "Sitios Desplegados"
        </p>
      </div>
    </div>
  );
}

export default PremiumDemosGallery;
